import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const FAQS = [
  {
    q: "Er Gjeldsveiviseren gratis å bruke?",
    a: "Ja, helt gratis — og det vil den alltid være. Siden er laget for å hjelpe, ikke for å selge noe. Alle lenkene går til offentlige tjenester som også er gratis.",
  },
  {
    q: "Er dere tilknyttet NAV eller andre offentlige etater?",
    a: "Nei. Gjeldsveiviseren er en uavhengig opplysningsportal. Vi samler lenker til offentlige sider som NAV, Skatteetaten og gjeldsregistrene, men innholdet hos dem er alltid det som gjelder.",
  },
  {
    q: "Hvor kan jeg få gratis gjeldsrådgivning?",
    a: "Alle kommuner i Norge tilbyr gratis og konfidensiell gjeldsrådgivning — ofte gjennom NAV-kontoret. Ring NAV på 55 55 33 33 eller kontakt ditt lokale NAV-kontor for å avtale en samtale.",
  },
  {
    q: "Hva er en gjeldsordning?",
    a: "En gjeldsordning er en lovfestet løsning for deg som har varige betalingsproblemer. Du får en nedbetalingsplan, vanligvis over fem år, og når perioden er over slettes resten av gjelden som er omfattet. Søknaden sendes til namsmannen i kommunen din.",
  },
  {
    q: "Lagres informasjonen jeg skriver inn?",
    a: "Nei. Budsjett- og gjeldstallene dine forblir i din egen nettleser og sendes aldri til noen server. Samtalen med AI-rådgiveren lagres også kun hos deg. Vi samler kun anonyme tall, som antall sidevisninger.",
  },
  {
    q: "Kan AI-rådgiveren gi meg bindende råd?",
    a: "Nei. AI-rådgiveren gir generell veiledning og kan peke deg til riktige offentlige sider, men svarene kan inneholde feil. For konkrete vedtak og personlige saker må du alltid snakke med NAV eller en gjeldsrådgiver.",
  },
];

export default function Faq() {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="py-20 lg:py-28 bg-surface-subtle/60 border-y border-border">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center"
        >
          <p className="text-xs uppercase tracking-[0.2em] font-mono font-semibold text-terracotta">Ofte stilte spørsmål</p>
          <h2 className="mt-3 font-serif font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-primary">
            Lurer du på noe?
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Korte svar på det de fleste spør om. Finner du ikke svaret, kan AI-rådgiveren hjelpe deg lenger ned på siden.
          </p>
        </motion.div>

        <div className="mt-10 space-y-3">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <motion.div
                key={f.q}
                data-testid="faq-item"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className={`bg-surface border rounded-2xl overflow-hidden transition-colors duration-300 ${isOpen ? "border-primary/40 shadow-md shadow-primary/5" : "border-border"}`}
              >
                <button
                  data-testid="faq-question"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between gap-4 text-left px-6 py-5"
                >
                  <span className="font-semibold text-base leading-snug">{f.q}</span>
                  <ChevronDown
                    size={18}
                    className={`shrink-0 text-muted-foreground transition-transform duration-300 ${isOpen ? "rotate-180 text-primary" : ""}`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <p data-testid="faq-answer" className="px-6 pb-6 text-sm text-muted-foreground leading-relaxed">
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
